import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, HostListener, ElementRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { StudentJobService } from '../../../core/services/student-job-management/student-job.service';

@Component({
  selector: 'app-public-job-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './public-job-list.component.html',
  styleUrl: './public-job-list.component.scss'
})
export class PublicJobListComponent implements OnInit, OnDestroy {
  jobs: any[] = [];
  isLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  hasMore = true;
  totalJobs = 0;

  // Search Filters
  searchQuery = '';
  locationQuery = '';
  selectedJobType = '';
  selectedWorkMode = '';

  private subscription = new Subscription();
  isBrowser: boolean;
  activeShareDropdown: string | null = null;

  constructor(
    private router: Router,
    private studentJobService: StudentJobService,
    @Inject(PLATFORM_ID) platformId: Object,
    private elementRef: ElementRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.activeShareDropdown = null;
    }
  }

  ngOnInit() {
    this.fetchJobs(true);
  }

  onSearch(event: any) {
    this.searchQuery = event.target.value;
    this.fetchJobs(true);
  }

  onLocationSearch(event: any) {
    this.locationQuery = event.target.value;
    this.fetchJobs(true);
  }

  onJobTypeChange(event: any) {
    this.selectedJobType = event.target.value;
    this.fetchJobs(true);
  }

  onWorkModeChange(mode: string) {
    this.selectedWorkMode = mode;
    this.fetchJobs(true);
  }

  fetchJobs(isInitial = false) {
    if (isInitial) {
      this.isLoading = true;
      this.currentPage = 1;
      this.jobs = [];
    } else {
      this.isLoadingMore = true;
    }

    this.subscription.add(
      this.studentJobService.getAllStudentsJobs(
        this.currentPage,
        9,
        'all',
        undefined,
        this.searchQuery,
        {
          location: this.locationQuery,
          job_type: this.selectedJobType,
          work_mode: this.selectedWorkMode,
          is_visible: true
        }
      ).subscribe({
        next: (res: any) => {
          if (res.Success && res.Data) {
            const newJobs = res.Data.jobs || [];
            this.jobs = [...this.jobs, ...newJobs];
            this.totalJobs = res.Data.total || 0;
            this.hasMore = this.jobs.length < this.totalJobs;
            if (newJobs.length < 9) {
              this.hasMore = false;
            }
          }
          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      })
    );
  }

  loadMore() {
    if (!this.hasMore || this.isLoadingMore) return;
    this.currentPage++;
    this.fetchJobs();
  }

  navigateToJob(jobId: string) {
    this.router.navigate(['/job', jobId]);
  }

  saveJob(job: any, event: Event) {
    event.stopPropagation();
    if (this.isBrowser) {
      localStorage.setItem('pending_save_job_id', job.job_id);
    }
    this.router.navigate(['/login'], {
      queryParams: { redirectUrl: '/student-dashboard/jobs/saved' }
    });
  }

  async shareJob(job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser || !job) return;

    const url = window.location.origin + '/job/' + job.job_id;
    const shareData = {
      title: `${job.job_title} at ${job.company_name} - BeExpert`,
      text: `Check out this amazing opportunity: ${job.job_title} at ${job.company_name}!`,
      url: url
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        this.toggleShareFallback(job.job_id);
      }
    } catch (err) {
      // Gracefully fail share in production
    }
  }

  toggleShareFallback(jobId: string) {
    if (this.activeShareDropdown === jobId) {
      this.activeShareDropdown = null;
    } else {
      this.activeShareDropdown = jobId;
    }
  }

  shareTo(platform: string, job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser) return;

    const url = encodeURIComponent(window.location.origin + '/job/' + job.job_id);
    const text = encodeURIComponent(`Check out this amazing job opportunity from BeExpert: ${job.job_title} at ${job.company_name}!`);

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${text}%20${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${url}&text=${text}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent('Amazing Job Opportunity')}&body=${text}%20${url}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    this.activeShareDropdown = null;
  }

  async copyLink(job: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isBrowser) return;
    try {
      const url = window.location.origin + '/job/' + job.job_id;
      await navigator.clipboard.writeText(url);
      const copyBtn = document.getElementById('copy-btn-' + job.job_id);
      if (copyBtn) {
        const originalText = copyBtn.innerHTML;
        copyBtn.innerHTML = '<span class="text-emerald-600 font-medium">Copied!</span>';
        setTimeout(() => copyBtn.innerHTML = originalText, 2000);
      }
    } catch (err) {
      // Gracefully fail copy
    }
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' years ago';

    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' months ago';

    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' days ago';

    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' hours ago';

    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' mins ago';

    return 'Just now';
  }

  trackByJobId(index: number, job: any): string {
    return job.job_id || index.toString();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

}
